import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { withPermissionCheck } from '../../hocs/withPermissionCheck';
import { APP_USER_ROLE } from '../../constants/UserRoles';
import ProtectedPageSidebar from '../../components/Sidebar/ProtectedPageSidebar';
import Container from '../../features/Editor/components/Container/Container';
import ExternalEditorContent from '../../features/Editor/components/ExternalEditorContent/ExternalEditorContent';
import RightSidebarContainer from '../../features/Editor/components/RightSidebarContainer/RightSidebarContainer';
import { SIDEBAR_STATE_LIST } from '../../features/Editor/const';
import useStoryComments from '../../features/Editor/hooks/useStoryComments';
import CommentsTab from '../../components/CommentsTab/CommentsTab';
import { useTranslation } from 'react-i18next';
import SidebarButton from '../newVideo/VideoSidebarMenu/SidebarButton/SidebarButton';
import useDownloadVideo from '../../hooks/useDownloadVideo';
import DownloadStatusIndicator from '../../features/Editor/components/DownloadStatusIndicator/DownloadStatusIndicator';
import supportedLanguages from '../../../common/constants/supportedLanguages.json';
import { ToastNotification } from '../../components/Toast/Toast';
import { useHistory } from 'react-router-dom';
import VideoService from '../../service/VideoService';
import { ScenesType } from '../../../common/apis/CmsAPI';
const commentsType = 3;

const ExternalEditor = ({ videoId, initialVideoDetails, userPermissions, role }) => {
    const [story, setStory] = useState(initialVideoDetails);
    const [miniRightSidebar, setMiniRightSidebar] = useState(false);
    const [sidebarState, setSidebarState] = useState(SIDEBAR_STATE_LIST.EDIT.TYPE);

    const history = useHistory();
    const originalLanguage = useMemo(() => {
        const originalLanguageId = story?.subtitlesMeta?.find(({ original }) => original)?.language;
        const originalLanguage = supportedLanguages.find(({ id }) => id === originalLanguageId);
        if (originalLanguage) {
            return originalLanguage;
        }
        return {};
    }, [])
    const { isCommentsLoading, comments, newComment, commentInit, setNewComment, loadComments } = useStoryComments({ storyId: videoId, commentsType, load: sidebarState === SIDEBAR_STATE_LIST.CHAT.TYPE });
    const { t } = useTranslation();


    function resourcedDownloadedCallback() {
        ToastNotification({
            iconName: 'success',
            message: t('download_modal.download_all_resources.succeeded'),
        });
    }
    const { handleDownloadMetadata, percentage, status } = useDownloadVideo({
        video: story,
        subtitlesLanguage: originalLanguage,
        callback: resourcedDownloadedCallback,
    });

    const sidebarOptions = useMemo(
        () => Object.values(SIDEBAR_STATE_LIST).filter(({ TYPE }) => {
            switch (TYPE) {
                case SIDEBAR_STATE_LIST.CHAT.TYPE:
                case SIDEBAR_STATE_LIST.EDIT.TYPE:
                    return true;
                default:
                    return false;
            }
        }),
        []
    );

    const toggleMiniSidebar = useCallback(state => {
        if (typeof state === 'boolean') setMiniRightSidebar(state);
        else setMiniRightSidebar(prevState => !prevState);
        sidebarState === SIDEBAR_STATE_LIST.CHAT.TYPE && setSidebarState(SIDEBAR_STATE_LIST.EDIT.TYPE);
    }, [sidebarState]);

    const switchBetweenTabs = (type) => {
        setSidebarState(type);
    }

    const onNewVersionUploaded = useCallback(async () => {
        const story = await VideoService.getVideoDetails(videoId, ScenesType.REGULAR);
        setStory(story);
    });


    useEffect(() => {
        const checkVideo = async () => {
            // Check if scenes have HLS & thumbnails, if not, show loading until done
            let story = {};
            if (initialVideoDetails) {
                story = { ...initialVideoDetails };
            } else {
                const result = await VideoService.getVideoDetails(videoId, ScenesType.REGULAR);
                story = { ...result };
            }
            setStory(story);
        }

        checkVideo();
    }, []);


    return (
        <>
            <ProtectedPageSidebar />
            <Container>
                <ExternalEditorContent story={story} onNewVersionUploaded={onNewVersionUploaded} />
                <RightSidebarContainer
                    mini={miniRightSidebar}
                    toggleMiniSidebar={toggleMiniSidebar}
                    tabs={{
                        options: sidebarOptions,
                        state: sidebarState,
                        onChangeTab: switchBetweenTabs,
                    }}
                    handleClose={() => history.goBack()}
                >
                    {sidebarState === SIDEBAR_STATE_LIST.EDIT.TYPE && (
                        <>
                            <SidebarButton label={t('download_modal.download_metadata_button')} icon='downloadVideo' mini={miniRightSidebar} onClick={handleDownloadMetadata} disabled={status !== 'pending'} />
                            <DownloadStatusIndicator status={status} percentage={percentage} />
                        </>
                    )}
                    {sidebarState === SIDEBAR_STATE_LIST.CHAT.TYPE && (
                        <CommentsTab
                            noCommentsMessage={!isCommentsLoading && comments?.length === 0 && t('storyboard.editor.no_comments_message')}
                            updateComments={loadComments}
                            isCommentsLoading={isCommentsLoading}
                            storyId={videoId}
                            comments={comments}
                            commentInit={commentInit}
                            newComment={newComment}
                            setNewComment={setNewComment}
                            highlightFirst={false}
                        />
                    )}
                </RightSidebarContainer>
            </Container>
        </>
    );
};


// TODO: remove superadmin role from withPermissionCheck
export default withPermissionCheck(ExternalEditor, true, [APP_USER_ROLE.VIDEO_EDITOR.TYPE, APP_USER_ROLE.SUPER_ADMIN.TYPE]);